import React from "react";

import {
  EntityReferencedError,
  ErrorEntitiesViolateRule,
  ErrorSchemaInvalid,
  ShallowEntity,
  ShallowInteraction,
} from "../types";
import { entityKindMap } from "../utils";
import {
  ExclamationMarkIcon as ExclamationMarkIcon,
  XIcon,
} from "@phosphor-icons/react";

export type RmsError =
  | EntityReferencedError
  | ErrorEntitiesViolateRule
  | ErrorSchemaInvalid
  | string;

interface ErrorRendererProps {
  error: RmsError;
  className?: string;
  onDismiss?: () => void;
}

const ErrorRenderer: React.FC<ErrorRendererProps> = ({
  error,
  className = "",
  onDismiss,
}) => {
  const baseClassName = `relative p-3 bg-red-50 border border-red-200 rounded-md ${className}`;
  // Reserve right-side gutter when a dismiss button is shown so the X
  // doesn't overlap long error text.
  const dismissButton = onDismiss ? (
    <button
      type="button"
      onClick={onDismiss}
      aria-label="Dismiss error"
      className="absolute top-2 right-2 text-red-500 hover:text-red-700"
    >
      <XIcon size={16} />
    </button>
  ) : null;
  const contentPaddingClass = onDismiss ? "pr-6" : "";

  // Handle simple string errors (fallback)
  if (typeof error === "string") {
    return (
      <div className={baseClassName}>
        {dismissButton}
        <div className={`flex items-start gap-2 ${contentPaddingClass}`}>
          <ExclamationMarkIcon
            size={16}
            className="text-red-600 mt-0.5 flex-shrink-0"
          />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  // Handle EntityReferencedError (delete operations)
  if ("referenced_by" in error) {
    const referencedBy = error.referenced_by;
    const isEntities = referencedBy.length > 0 && "kind" in referencedBy[0];

    return (
      <div className={baseClassName}>
        {dismissButton}
        <div className={`flex items-start gap-2 ${contentPaddingClass}`}>
          <ExclamationMarkIcon
            size={16}
            className="text-red-600 mt-0.5 flex-shrink-0"
          />
          <div className="flex-1">
            <p className="text-sm text-red-600 font-medium mb-2">
              {error.message}
            </p>

            <div className="text-sm text-red-700">
              <p className="font-medium mb-1">
                Referenced by {referencedBy.length}{" "}
                {isEntities ? "entities" : "interactions"}:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                {referencedBy.map((item, index) => {
                  if (isEntities) {
                    const entity = item as ShallowEntity;
                    return (
                      <li key={`${entity.id}-${index}`} className="text-xs">
                        <span className="font-medium">{entity.name}</span>
                        <span className="text-red-500 ml-1">
                          ({entityKindMap[entity.kind]?.label || entity.kind})
                        </span>
                      </li>
                    );
                  } else {
                    const interaction = item as ShallowInteraction;
                    return (
                      <li
                        key={`${interaction.id}-${index}`}
                        className="text-xs"
                      >
                        <span className="font-medium">{interaction.title}</span>
                        <span className="text-red-500 ml-1">
                          ({interaction.meeting_type})
                        </span>
                      </li>
                    );
                  }
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle ErrorEntitiesViolateRule (schema validation with entities)
  if ("entities" in error) {
    return (
      <div className={baseClassName}>
        {dismissButton}
        <div className={`flex items-start gap-2 ${contentPaddingClass}`}>
          <ExclamationMarkIcon
            size={16}
            className="text-red-600 mt-0.5 flex-shrink-0"
          />
          <div className="flex-1">
            <p className="text-sm text-red-600 font-medium mb-2">
              {error.message}
            </p>

            {error.entities.length > 0 && (
              <div className="text-sm text-red-700">
                <p className="font-medium mb-1">
                  Affected entities ({error.entities.length}):
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  {error.entities.map((entity, index) => (
                    <li key={`${entity.id}-${index}`} className="text-xs">
                      <span className="font-medium">{entity.name}</span>
                      <span className="text-red-500 ml-1">
                        ({entityKindMap[entity.kind]?.label || entity.kind})
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Handle ErrorSchemaInvalid (general schema validation)
  if ("message" in error) {
    return (
      <div className={baseClassName}>
        {dismissButton}
        <div className={`flex items-start gap-2 ${contentPaddingClass}`}>
          <ExclamationMarkIcon
            size={16}
            className="text-red-600 mt-0.5 flex-shrink-0"
          />
          <p className="text-sm text-red-600">{error.message}</p>
        </div>
      </div>
    );
  }

  // Fallback for unknown error structure
  return (
    <div className={baseClassName}>
      {dismissButton}
      <div className={`flex items-start gap-2 ${contentPaddingClass}`}>
        <ExclamationMarkIcon
          size={16}
          className="text-red-600 mt-0.5 flex-shrink-0"
        />
        <p className="text-sm text-red-600">An unexpected error occurred.</p>
      </div>
    </div>
  );
};

export default ErrorRenderer;
